import { supabase } from '../../client';
import { logError } from '@/lib/sentry';
import type {
  OnboardingSession,
  LatestOnboardingResult,
  VerificationStatus,
  CompleteAndVerifyResult,
  LatestVerificationResult,
} from '../types/onboarding';
import type { DepositBankCode } from '@/supabase/deposit/banks';

// 백엔드 RPC가 RAISE EXCEPTION 메시지로 던지는 실패를 UI 분기 종류로 환원(순수 함수).
export type OnboardingErrorKind =
  | { kind: 'gateway_busy'; until: Date }       // 타 셀러 게이트웨이 점유
  | { kind: 'active_session_exists' }           // 본인 다른 계좌 진행 중
  | { kind: 'unsupported_bank' }                // P0=Woori only
  | { kind: 'not_allowlisted' }                 // Type B(하나) 공개 전 allowlist 미포함
  | { kind: 'account_owned_by_other' }          // 계좌가 타 계정 소유
  | { kind: 'account_bank_mismatch' }           // 기존 계좌 은행 불일치
  | { kind: 'not_ready' }                        // 인증코드/등록확인 전 완료 시도
  | { kind: 'session_expired' }                  // lease 만료 후 완료 시도(Type B 무응답 등)
  | { kind: 'unknown'; message: string };

export function parseOnboardingError(message: string): OnboardingErrorKind {
  const busy = message.match(/gateway_busy_until:(\d+)/);
  if (busy) {
    const until = new Date(Number(busy[1]) * 1000);
    // 비정상 epoch(Invalid Date)면 gateway_busy로 오인하지 말고 unknown 폴백.
    if (Number.isFinite(until.getTime())) return { kind: 'gateway_busy', until };
  }
  if (message.includes('active_session_exists')) return { kind: 'active_session_exists' };
  if (message.includes('not_allowlisted')) return { kind: 'not_allowlisted' };
  if (message.includes('unsupported_bank')) return { kind: 'unsupported_bank' };
  if (message.includes('account_owned_by_other')) return { kind: 'account_owned_by_other' };
  if (message.includes('account_bank_mismatch')) return { kind: 'account_bank_mismatch' };
  if (message.includes('session_expired')) return { kind: 'session_expired' };
  if (message.includes('not_ready')) return { kind: 'not_ready' };
  return { kind: 'unknown', message };
}

// start_onboarding(bank_code, account_number) → 세션(또는 RAISE EXCEPTION).
export async function startOnboarding(
  bankCode: DepositBankCode,
  accountNumber: string,
): Promise<OnboardingSession> {
  const digits = accountNumber.replace(/\D/g, '');
  const { data, error } = await supabase.rpc('start_onboarding', {
    p_bank_code: bankCode,
    p_account_number: digits,
  });
  if (error) { logError('startOnboarding', error); throw error; }
  return data as OnboardingSession;
}

export async function cancelOnboarding(sessionId: number): Promise<OnboardingSession> {
  const { data, error } = await supabase.rpc('cancel_onboarding', {
    p_session_id: sessionId,
  });
  if (error) { logError('cancelOnboarding', error); throw error; }
  return data as OnboardingSession;
}

export async function completeOnboarding(sessionId: number): Promise<OnboardingSession> {
  const { data, error } = await supabase.rpc('complete_onboarding', {
    p_session_id: sessionId,
  });
  if (error) { logError('completeOnboarding', error); throw error; }
  return data as OnboardingSession;
}

// 위저드 폴링: 최신 세션 + 서버시간.
export async function getLatestOnboarding(): Promise<LatestOnboardingResult> {
  const { data, error } = await supabase.rpc('get_latest_onboarding_session');
  if (error) { logError('getLatestOnboarding', error); throw error; }
  return data as LatestOnboardingResult;
}

// 온보딩 완료 + apick 1원 검증 트리거(authenticated Edge Function 경계).
// complete_onboarding(소유권/provisional) → reserve-before-call → apick → finalize를 서버가 수행.
export async function completeAndVerify(sessionId: number): Promise<CompleteAndVerifyResult> {
  const { data, error } = await supabase.functions.invoke('complete-and-verify', {
    body: { session_id: sessionId },
  });
  if (error) {
    logError('completeAndVerify', error);
    // FunctionsHttpError.message는 "Edge Function returned a non-2xx status code" 고정 문자열이라
    // 실제 RPC 실패 사유(session_expired/not_ready/account_owned_by_other 등)는 응답 body에만 있다.
    // body {ok:false, error}에서 진짜 메시지를 꺼내 throw → parseOnboardingError가 분류(죽은 매핑 부활).
    let message = (error as Error)?.message ?? 'complete_failed';
    const ctx = (error as { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const body = await ctx.json();
        if (body && typeof body.error === 'string') message = body.error;
      } catch { /* body 파싱 실패 → 원 message 유지 */ }
    }
    throw new Error(message);
  }
  return data as CompleteAndVerifyResult;
}

// 검증 attempt sanitized 폴링(verifying/confirmed/failed 파생). 만료는 server-owned.
export async function getLatestVerification(sessionId: number): Promise<LatestVerificationResult> {
  const { data, error } = await supabase.rpc('get_latest_verification_attempt', {
    p_session_id: sessionId,
  });
  if (error) { logError('getLatestVerification', error); throw error; }
  return data as LatestVerificationResult;
}

// 계좌별 검증 상태(RLS dav_owner_select로 본인 행만). 배지 표시용.
export async function listAccountVerifications(): Promise<Record<number, VerificationStatus>> {
  const { data, error } = await supabase
    .from('deposit_account_verification')
    .select('seller_deposit_account_id, status');
  if (error) { logError('listAccountVerifications', error); throw error; }
  const map: Record<number, VerificationStatus> = {};
  for (const row of data ?? []) {
    map[row.seller_deposit_account_id as number] = row.status as VerificationStatus;
  }
  return map;
}
